import http from '../../../Utils/http';
import httpUploader from '../../../Utils/httpUploader';
import { getCategoryDisplayName } from '../../../Utils/zoneCategoryDetection';

const getZoneCostFromPricing = (zoneId, costData, displayId = null) => {
  const pricedZones = costData?.totalCampaignPrice?.zones || [];
  if (!Array.isArray(pricedZones) || pricedZones.length === 0) return 0;

  const byDisplayAndZone = pricedZones.find(
    (z) =>
      String(z?.id) === String(zoneId) &&
      z?.display_id !== undefined &&
      z?.display_id !== null &&
      displayId !== null &&
      String(z.display_id) === String(displayId)
  );
  if (byDisplayAndZone?.price !== undefined && byDisplayAndZone?.price !== null) {
    return parseFloat(byDisplayAndZone.price) || 0;
  }

  const byZoneOnly = pricedZones.find((z) => String(z?.id) === String(zoneId));
  if (byZoneOnly?.price !== undefined && byZoneOnly?.price !== null) {
    return parseFloat(byZoneOnly.price) || 0;
  }

  return 0;
};

const buildZonesDataPayload = ({
  step2,
  listOfCampaignZones,
  assignmentMappings,
  mediaLibrary,
  costData,
}) => {
  const isNewFlow = assignmentMappings && Object.keys(assignmentMappings).length > 0;

  if (isNewFlow) {
    const allMappings = [];
    const validDisplayIds = new Set(step2?.map((d) => d.id) || []);

    Object.values(assignmentMappings).forEach((zoneMapping) => {
      const mappings = zoneMapping?.mappings || [];

      mappings.forEach((mapping) => {
        if (!validDisplayIds.has(mapping.display_id)) return;

        const media = mediaLibrary?.find(
          (m) => String(m.fileUrl?.mediaId || m.mediaId) === String(mapping.media_id)
        );
        const display = step2?.find((d) => d.id === mapping.display_id);
        if (!display) return;

        const displayZones = display?.rateSchedule?.zones || display?.zones || [];
        const zoneDetails = displayZones.find((z) => z.id === mapping.zone_id);
        const normalizedZoneType =
          zoneMapping.zoneType === 'default' || zoneMapping.zoneType === null || zoneMapping.zoneType === ''
            ? null
            : zoneMapping.zoneType;

        allMappings.push({
          display_id: mapping.display_id,
          id: mapping.zone_id,
          zone_id: mapping.zone_id,
          media_id: mapping.media_id,
          zone_type: normalizedZoneType,
          media: media?.fileUrl?.fileUrl || media?.preview || '',
          name: zoneDetails?.zoneName || 'Zone',
          zoneName: zoneDetails?.zoneName || 'Zone',
          cost: getZoneCostFromPricing(mapping.zone_id, costData, mapping.display_id),
          ...(zoneDetails || {}),
        });
      });
    });

    return allMappings;
  }

  const validDisplayIds = new Set(step2?.map((d) => d.id) || []);
  return listOfCampaignZones?.filter((z) => validDisplayIds.has(z.display_id)) || [];
};

const buildEditZonesPayload = (zonesData = [], lockedDisplayIds = [], step2 = []) => {
  const lockedDisplayIdSet = new Set((lockedDisplayIds || []).map((id) => String(id)));
  const usedByDisplayZone = new Map();

  (zonesData || []).forEach((zone) => {
    const displayId = zone?.display_id;
    const zoneId = zone?.zone_id ?? zone?.id;
    if (displayId === undefined || displayId === null || zoneId === undefined || zoneId === null) return;
    usedByDisplayZone.set(`${displayId}-${zoneId}`, zone);
  });

  const allZonesWithUsage = [];
  (step2 || []).forEach((display) => {
    const displayId = display?.id;
    const displayZones = display?.rateSchedule?.zones || display?.zones || [];
    displayZones.forEach((z) => {
      const zoneId = z?.id ?? z?.zone_id;
      if (displayId === undefined || displayId === null || zoneId === undefined || zoneId === null) return;
      allZonesWithUsage.push({
        display_id: displayId,
        id: zoneId,
        zone_id: zoneId,
      });
    });
  });

  const sourceZones = allZonesWithUsage.length > 0 ? allZonesWithUsage : (zonesData || []);

  return sourceZones.map((zone) => {
    const displayId = zone?.display_id;
    const zoneId = zone?.zone_id ?? zone?.id;
    const usedZone = usedByDisplayZone.get(`${displayId}-${zoneId}`);
    const isUsed = !!usedZone;
    const isExistingZone = lockedDisplayIdSet.has(String(displayId));

    if (isExistingZone || !isUsed) {
      return {
        id: zoneId,
        display_id: displayId,
        isEdit: false,
        isUsed,
      };
    }

    return {
      id: zoneId,
      display_id: displayId,
      isEdit: true,
      isUsed: true,
      media: usedZone?.media || '',
      cost: usedZone?.cost ?? 0,
    };
  });
};

const CampaignStepsMangment = {
  name: 'CampaignStepsMangment',
  state: {
    displaysData: null,
    allMapDisplays: null,
    snapShot: null,
    error: null,
    tableHeader: [
      'Name',
      'Address',
      'Industry',
      'Display Owner',
      'Visibility',
      'Visibility Status',
      'Layout',
    ],

    loadingUpload: [],
    loading: false,
    step1: null,
    step2: null,
    step3: null,
    step4: null,
    listOfCampaignZones: null,
    selectedZones: [],
    mediaLibrary: [],
    tempCampaignId: null,
    assignmentMappings: {},
    step3SubStep: 'upload',
  },
  reducers: {
    injectStepData(state, payload) {
      return {
        ...state,
        ...payload,
      };
    },
    updateSnapShot(state, payload) {
      return {
        ...state,
        snapShot: payload,
      };
    },

    clearStat(state) {
      return {
        ...state,
        loadingUpload: [],
        displaysData: null,
        allMapDisplays: null,
        error: null,
        loading: false,
        step1: null,
        step2: null,
        step3: null,
        step4: null,
        snapShot: null,
        listOfCampaignZones: null,
        selectedZones: [],
        mediaLibrary: [],
        tempCampaignId: null,
        assignmentMappings: {},
        step3SubStep: 'upload',
        assignmentLog: [],
      };
    },

    clearScreenShot(state) {
      return {
        ...state,
        snapShot: null,
      };
    },

    processError(state, payload) {
      return {
        ...state,
        error: payload,
      };
    },
    appendSelectedDisplay(state, payload) {
      return {
        ...state,
        selectedZones: payload,
      };
    },
  },
  effects: (dispatch) => ({
    async storeStepData(data, state) {
      const prevStep2 = state.CampaignStepsMangment.step2;
      const newStep2 = Array.isArray(data?.step2)
        ? data.step2.filter((d) => d && (d.id !== undefined && d.id !== null))
        : data?.step2;

      const displaysChanged =
        newStep2 &&
        JSON.stringify(newStep2.map((d) => d.id).sort()) !==
        JSON.stringify((prevStep2 || []).filter((d) => d && (d.id !== undefined && d.id !== null)).map((d) => d.id).sort());

      if (displaysChanged) {
        this.injectStepData({ selectedZones: [] });
      }
      this.injectStepData({
        ...data,
        ...(Array.isArray(newStep2) ? { step2: newStep2 } : {}),
      });
    },

    async addCampaignZoneToList(data, state) {
      const { listOfCampaignZones } = state.CampaignStepsMangment;
      if (!data.media) {
        this.processError([{ id: data.id }]);
        return;
      }

      if (!listOfCampaignZones) {
        this.injectStepData({
          listOfCampaignZones: [data],
        });
      } else if (
        listOfCampaignZones?.find(
          (item) => item.id === data.id && item.display_id === data.display_id
        )
      ) {
        const newLS = listOfCampaignZones?.filter(
          (item) => !(item.id === data.id && item.display_id === data.display_id)
        );
        this.injectStepData({
          listOfCampaignZones: [...newLS, data],
        });
      } else {
        this.injectStepData({
          listOfCampaignZones: [...listOfCampaignZones, data],
        });
      }
    },

    async UploadToAws({ formData, id }) {
      try {
        this.injectStepData({
          loadingUpload: [id],
        });
        const response = await httpUploader.post('/utility/upload', formData);
        this.injectStepData({
          loadingUpload: [],
        });
        return response.data;
      } catch (err) {
        this.processError([{ id: id }]);
        this.injectStepData({
          loading: false,
        });
      }
    },

    getOrCreateTempCampaignId(state) {
      let { tempCampaignId } = state.CampaignStepsMangment;

      if (!tempCampaignId) {
        tempCampaignId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = Math.random() * 16 | 0;
          const v = c === 'x' ? r : ((r & 0x3) | 0x8);
          return v.toString(16);
        });

        this.injectStepData({
          tempCampaignId,
        });
      }

      return tempCampaignId;
    },

    async fetchMediaBySessionId({ sessionId }, state) {
      try {
        if (!sessionId) {
          return { success: false, media: [] };
        }

        const userInfo = JSON.parse(localStorage.getItem('userInfo'));
        const userId = userInfo?.id;

        if (!userId) {
          throw new Error('User information not found');
        }

        const url = `savvy/campaign/media/${sessionId}`;

        const response = await http.get(url, {
          params: {
            user_id: userId,
          },
        });

        const mediaArray = response.data?.data?.media || response.data?.media || [];

        const transformedMedia = mediaArray.map((media) => {
          const fileId = media.mediaId || crypto.randomUUID();

          const fileUrlObj = media.fileUrl || {};
          const actualFileUrl = typeof fileUrlObj === 'object' && fileUrlObj !== null
            ? (fileUrlObj.fileUrl || fileUrlObj.url || null)
            : (typeof media.fileUrl === 'string' ? media.fileUrl : null);
          const thumbnailUrl = fileUrlObj.thumbnailUrl || media.thumbnailUrl || null;

          const rawZoneType = media.zoneType || fileUrlObj.zoneType || null;
          const normalizedZoneType =
            (rawZoneType === 'default' || rawZoneType === '' || rawZoneType === null || rawZoneType === undefined)
              ? null
              : String(rawZoneType).toLowerCase().trim();

          return {
            id: fileId,
            name: fileUrlObj.fileName || media.fileName || 'Unknown',
            size: parseInt(fileUrlObj.fileSize || media.fileSize || 0),
            type: fileUrlObj.mimeType || media.mimeType || '',
            file: null,
            zoneType: normalizedZoneType,
            zoneDisplayName: normalizedZoneType ? (media.zoneDisplayName || null) : null,
            preview: actualFileUrl || thumbnailUrl || null,
            status: 'uploaded',
            uploadProgress: 100,
            mediaId: media.mediaId || fileUrlObj.mediaId,
            fileUrl: {
              mediaId: media.mediaId || fileUrlObj.mediaId,
              fileUrl: actualFileUrl,
              fileName: fileUrlObj.fileName || media.fileName,
              fileSize: fileUrlObj.fileSize || media.fileSize,
              mimeType: fileUrlObj.mimeType || media.mimeType,
              fileType: fileUrlObj.fileType || media.fileType,
              width: fileUrlObj.width || media.width,
              height: fileUrlObj.height || media.height,
              aspectRatio: fileUrlObj.aspectRatio || media.aspectRatio,
              duration: fileUrlObj.duration || media.duration,
              thumbnailUrl: thumbnailUrl,
              thumbnailPath: fileUrlObj.thumbnailPath || media.thumbnailPath,
              zoneType: normalizedZoneType,
              uploadSessionId: sessionId,
            },
            resolution: media.resolution || (fileUrlObj.width && fileUrlObj.height
              ? `${fileUrlObj.width}x${fileUrlObj.height}`
              : (media.width && media.height ? `${media.width}x${media.height}` : null)),
            duration: fileUrlObj.duration || media.duration || null,
            error: null,
          };
        });

        this.injectStepData({
          mediaLibrary: transformedMedia,
        });

        return {
          success: true,
          media: transformedMedia,
        };
      } catch (err) {
        return {
          success: false,
          media: [],
          error: err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to fetch media',
        };
      }
    },

    async fetchMediaByCampaignId({ campaignId }, state) {
      try {
        if (!campaignId) {
          return { success: false, media: [] };
        }

        const userInfo = JSON.parse(localStorage.getItem('userInfo'));
        const userId = userInfo?.id;

        if (!userId) {
          throw new Error('User information not found');
        }

        const url = `savvy/campaign/media/campaign/${campaignId}`;
        const response = await http.get(url, {
          params: {
            user_id: userId,
          },
        });

        const mediaArray = response.data?.data?.media || response.data?.media || [];

        const transformedMedia = mediaArray.map((media) => {
          const fileId = media.mediaId || crypto.randomUUID();
          const fileUrlObj = media.fileUrl || {};
          const actualFileUrl =
            typeof fileUrlObj === 'object' && fileUrlObj !== null
              ? fileUrlObj.fileUrl || fileUrlObj.url || null
              : typeof media.fileUrl === 'string'
                ? media.fileUrl
                : null;

          const thumbnailUrl = fileUrlObj.thumbnailUrl || media.thumbnailUrl || null;

          const rawZoneType = media.zoneType || fileUrlObj.zoneType || null;
          const normalizedZoneType =
            (rawZoneType === 'default' || rawZoneType === '' || rawZoneType === null || rawZoneType === undefined)
              ? null
              : String(rawZoneType).toLowerCase().trim();

          return {
            id: fileId,
            name: fileUrlObj.fileName || media.fileName || 'Unknown',
            size: parseInt(fileUrlObj.fileSize || media.fileSize || 0),
            type: fileUrlObj.mimeType || media.mimeType || '',
            file: null,
            zoneType: normalizedZoneType,
            zoneDisplayName: normalizedZoneType ? (media.zoneDisplayName || null) : null,
            preview: actualFileUrl || thumbnailUrl || null,
            status: 'uploaded',
            uploadProgress: 100,
            mediaId: media.mediaId || fileUrlObj.mediaId,
            fileUrl: {
              mediaId: media.mediaId || fileUrlObj.mediaId,
              fileUrl: actualFileUrl,
              fileName: fileUrlObj.fileName || media.fileName,
              fileSize: fileUrlObj.fileSize || media.fileSize,
              mimeType: fileUrlObj.mimeType || media.mimeType,
              fileType: fileUrlObj.fileType || media.fileType,
              width: fileUrlObj.width || media.width,
              height: fileUrlObj.height || media.height,
              aspectRatio: fileUrlObj.aspectRatio || media.aspectRatio,
              duration: fileUrlObj.duration || media.duration,
              thumbnailUrl: thumbnailUrl,
              thumbnailPath: fileUrlObj.thumbnailPath || media.thumbnailPath,
              zoneType: normalizedZoneType,
              uploadSessionId: campaignId,
            },
            resolution: media.resolution || (fileUrlObj.width && fileUrlObj.height
              ? `${fileUrlObj.width}x${fileUrlObj.height}`
              : (media.width && media.height ? `${media.width}x${media.height}` : null)),
            duration: fileUrlObj.duration || media.duration || null,
            error: null,
          };
        });

        this.injectStepData({
          mediaLibrary: transformedMedia,
        });

        return {
          success: true,
          media: transformedMedia,
        };
      } catch (err) {
        return {
          success: false,
          media: [],
          error:
            err.response?.data?.error ||
            err.response?.data?.message ||
            err.message ||
            'Failed to fetch media',
        };
      }
    },

    async uploadMediaToLibrary({ formData, onUploadProgress, duration }, state) {
      try {
        const tempCampaignId = this.getOrCreateTempCampaignId(state);

        formData.append('tempCampaignId', tempCampaignId);
        const userInfo = JSON.parse(localStorage.getItem('userInfo'));
        const onBehalf = JSON.parse(localStorage.getItem('onBehalfName'));
        const orgId = userInfo?.org_id;
        formData.append('user_id', userInfo?.id);
        formData.append('on_behalf', onBehalf);
        formData.append('org_id', orgId);
        formData.append('duration', duration);

        const config = {
          onUploadProgress: (progressEvent) => {
            if (onUploadProgress && progressEvent.lengthComputable) {
              onUploadProgress(progressEvent);
            }
          },
        };

        const url = '/savvy/campaign/media/upload';
        const response = await httpUploader.post(
          url,
          formData,
          config,
        );

        const { mediaLibrary = [] } = state.CampaignStepsMangment;
        const file = formData.get('file');
        const newMedia = {
          mediaId: response.data.mediaId || response.data.id || response.data.data?.id,
          fileUrl: response.data.fileUrl || response.data.data?.url || response.data.data || response.data.url,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          uploadedAt: new Date().toISOString(),
          zoneType: null,
          resolution: response.data.resolution || response.data.data?.resolution,
          duration: response.data.duration || response.data.data?.duration,
          ...(response.data.metadata || response.data.data?.metadata || {}),
        };

        this.injectStepData({
          mediaLibrary: [...mediaLibrary, newMedia],
        });

        return {
          mediaId: newMedia.mediaId,
          fileUrl: newMedia.fileUrl,
          metadata: newMedia,
        };
      } catch (err) {
        const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Upload failed';
        throw new Error(errorMessage);
      }
    },

    async tagMediaZone({ campaignId, mediaId, zoneType }, state) {
      try {
        if (!mediaId) {
          throw new Error('Media ID is required');
        }

        const userInfo = JSON.parse(localStorage.getItem('userInfo'));
        const userId = userInfo?.id;

        if (!userId) {
          throw new Error('User information not found');
        }

        let formattedZoneType = zoneType && zoneType !== '' && zoneType !== 'default'
          ? String(zoneType).toLowerCase().trim()
          : null;

        const currentMediaLibrary = state.CampaignStepsMangment?.mediaLibrary || [];
        const currentMedia = currentMediaLibrary.find(
          m => String(m.fileUrl?.mediaId || m.mediaId) === String(mediaId)
        );
        const oldZoneType = currentMedia?.zoneType || currentMedia?.fileUrl?.zoneType;
        const normalizedOldZoneType = (oldZoneType === 'default' || oldZoneType === null || oldZoneType === '')
          ? null
          : String(oldZoneType).toLowerCase().trim();

        if (normalizedOldZoneType && normalizedOldZoneType !== formattedZoneType) {
          const currentAssignmentMappings = state.CampaignStepsMangment?.assignmentMappings || {};
          const oldZoneMappings = currentAssignmentMappings[normalizedOldZoneType];

          if (oldZoneMappings && oldZoneMappings.mappings) {
            const updatedMappings = oldZoneMappings.mappings.filter(
              mapping => String(mapping.media_id) !== String(mediaId)
            );

            const updatedAssignmentMappings = { ...currentAssignmentMappings };

            if (updatedMappings.length === 0) {
              delete updatedAssignmentMappings[normalizedOldZoneType];
            } else {
              const uniqueDisplayIds = new Set(updatedMappings.map(m => m.display_id));
              updatedAssignmentMappings[normalizedOldZoneType] = {
                ...oldZoneMappings,
                mappings: updatedMappings,
                assignmentsCreated: updatedMappings.length,
                displayCount: uniqueDisplayIds.size,
              };
            }

            this.injectStepData({
              assignmentMappings: updatedAssignmentMappings,
            });
          }
        }

        const url = `savvy/campaign/media/tag/${mediaId}`;
        const payloadFor = (zone_type) => ({
          zone_type,
          user_id: userId,
        });

        let response;
        try {
          response = await http.patch(url, payloadFor(formattedZoneType));
        } catch (err) {
          const backendMsg = err?.response?.data?.error || err?.response?.data?.message || '';
          const isInvalidZoneType = String(backendMsg).toLowerCase().includes('invalid zone type');

          if (formattedZoneType === 'led' && isInvalidZoneType) {
            response = await http.patch(url, payloadFor('custom'));
          } else {
            throw err;
          }
        }
        
        const { mediaLibrary = [] } = state.CampaignStepsMangment;
        const updatedLibrary = mediaLibrary.map(media =>
          media.mediaId === mediaId
            ? {
              ...media,
              zoneType: formattedZoneType,
              zoneDisplayName: formattedZoneType ? (media.zoneDisplayName || formattedZoneType) : null
            }
            : media
        );

        this.injectStepData({
          mediaLibrary: updatedLibrary,
        });

        return {
          success: true,
          data: response.data,
          zoneType: formattedZoneType
        };
      } catch (err) {
        const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to tag media';
        throw new Error(errorMessage);
      }
    },

    async deleteMediaFromLibrary({ mediaId }, state) {
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo'));
        const userId = userInfo?.id;

        if (!userId) {
          throw new Error('User information not found');
        }

        await http.delete(`/savvy/campaign/media/${mediaId}`, {
          data: {
            user_id: userId,
          },
        });

        const { mediaLibrary = [] } = state.CampaignStepsMangment;
        const updatedLibrary = mediaLibrary.filter(media => media.mediaId !== mediaId);

        this.injectStepData({
          mediaLibrary: updatedLibrary,
        });

        return { success: true };
      } catch (err) {
        const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Delete failed';
        throw new Error(errorMessage);
      }
    },

    async deleteAllMediaBySessionId({ sessionId }, state) {
      try {
        if (!sessionId) {
          throw new Error('Session ID is required');
        }

        const userInfo = JSON.parse(localStorage.getItem('userInfo'));
        const userId = userInfo?.id;

        if (!userId) {
          throw new Error('User information not found');
        }

        const url = `savvy/campaign/media/session/${sessionId}`;

        const response = await http.delete(url, {
          data: {
            user_id: userId,
          },
        });

        this.injectStepData({
          mediaLibrary: [],
        });

        return { success: true, data: response.data };
      } catch (err) {
        const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to delete all media';
        throw new Error(errorMessage);
      }
    },
    async UploadImageToAws(image) {
      try {
        const formData = new FormData();
        formData.append('file', image);
        const response = await httpUploader.post('/utility/upload/image', formData);
        if (response?.data?.url) {
          this.updateSnapShot(response.data.url);
        }
        return response.data.url;
      } catch (err) {
        this.injectStepData({
          loading: false,
        });
      }
    },

    async UploadNameImageToAws(image) {
      try {
        const response = await httpUploader.post('/utility/upload/NameImage', image);
        return response.data.data;
      } catch (err) {
        this.injectStepData({
          loading: false,
        });
      }
    },

    async Update_Proof_of_Play({ id, urls, adv_id }) {
      try {
        const user = JSON.parse(localStorage.getItem('userInfo'));
        const onBehalf = JSON.parse(localStorage.getItem('onBehalfName'));
        const payload = {
          user_id: user?.id,
          on_behalf: onBehalf,
          adv_id: adv_id,
          urls: urls,
        }
        const response = await httpUploader.put(
          payload,
          { headers: { 'Content-Type': 'application/json' } }
        );
        return response.data.data;
      } catch (err) {
      }
    },

    async Update_Proof_of_Play_Requested({ id, adv_id }) {
      try {
        const user = JSON.parse(localStorage.getItem('userInfo'));
        const onBehalf = JSON.parse(localStorage.getItem('onBehalfName'));
        const payload = {
          user_id: user?.id,
          on_behalf: onBehalf,
          adv_id: adv_id,
        }
        const response = await httpUploader.put(
          payload,
          { headers: { 'Content-Type': 'application/json' } }
        );
        return response.data.data;
      } catch (err) {
      }
    },

    async getInvitedToDisplayList(data, state) {
      const { page, size, search, filter, sorted_by, sort, camp_id } = data;
      const user = state?.user?.data;
      this.injectStepData({
        loading: true,
      });
      let url = `savvy/displays/adv-displays/${user?.org_id}?page=${page}&size=${size}&isCampaign=true`;
      if (camp_id !== undefined) {
        url = url + `&camp_id=${encodeURIComponent(camp_id)}`;
      }
      if (search) {
        if (
          search.toLowerCase() === 'inactive' ||
          search.toLowerCase() === 'active'
        ) {
          url = url + `&active=${search.toLowerCase() === 'inactive' ? 0 : 1}`;
        } else {
          url = url + `&search=${search}&filter=${filter}`;
        }
      }
      if (filter?.visibility) {
        url += `&visibility=${filter.visibility}`;
      }

      if (sorted_by) {
        url = url + `&sorted_by=${sorted_by}`;
      }
      if (sort) {
        url = url + `&sort=${sort}`;
      }

      if (filter?.industry?.length) {
        filter.industry.forEach(ind => {
          url += `&industry[]=${encodeURIComponent(ind)}`;
        });
      }
      try {
        const response = await http.get(url);
        this.injectStepData({
          displaysData: response.data.data,
          loading: false,
        });
      } catch (err) {
        this.injectStepData({
          error: 'some thing went wrong',
          loading: false,
        });
      }
    },

    async getAllDisplaysForMap(data, state) {
      const { search, filter, sorted_by, sort, camp_id } = data;
      const user = state?.user?.data;
      let url = `savvy/displays/adv-displays/${user?.org_id}?page=1&size=10000&isCampaign=true`;
      if (camp_id !== undefined) {
        url = url + `&camp_id=${encodeURIComponent(camp_id)}`;
      }
      
      if (search) {
        if (
          search.toLowerCase() === 'inactive' ||
          search.toLowerCase() === 'active'
        ) {
          url = url + `&active=${search.toLowerCase() === 'inactive' ? 0 : 1}`;
        } else {
          url = url + `&search=${search}&filter=${filter}`;
        }
      }
      if (filter?.visibility) {
        url += `&visibility=${filter.visibility}`;
      }

      if (sorted_by) {
        url = url + `&sorted_by=${sorted_by}`;
      }
      if (sort) {
        url = url + `&sort=${sort}`;
      }

      if (filter?.industry?.length) {
        filter.industry.forEach(ind => {
          url += `&industry[]=${encodeURIComponent(ind)}`;
        });
      }

      try {
        const response = await http.get(url);
        this.injectStepData({
          allMapDisplays: response.data.data,
        });
      } catch (err) {
        console.error('Failed to fetch displays for map:', err);
      }
    },

    async postCampaignHandler(data, state) {
      const { campComment, totalCost, costData, isEdit = false, campaignId = null, lockedDisplayIds = [] } = data;
      const { step1, step2, listOfCampaignZones, assignmentMappings, mediaLibrary, tempCampaignId } = state.CampaignStepsMangment;
      const onBehalf = JSON.parse(localStorage.getItem('onBehalfName'));
      const userInfo = state.user.data;
      const orgId = JSON.parse(localStorage.getItem('userInfo')).org_id;
      const orgIds = step2?.map((item) => {
        return item?.org_id;
      });

      const zonesData = buildZonesDataPayload({
        step2,
        listOfCampaignZones,
        assignmentMappings,
        mediaLibrary,
        costData,
      });
      const uniqueZoneAssignments = new Map();
      (zonesData || []).forEach((z) => {
        const key = `${z?.display_id}-${z?.zone_id ?? z?.id}`;
        if (!uniqueZoneAssignments.has(key)) {
          uniqueZoneAssignments.set(key, z);
        }
      });
      const payloadCost = Array.from(uniqueZoneAssignments.values()).reduce((sum, z) => {
        const n = Number(z?.cost ?? 0);
        return sum + (Number.isFinite(n) ? n : 0);
      }, 0);
      const isNewFlow = assignmentMappings && Object.keys(assignmentMappings).length > 0;

      const createCampaignPayload = {
        isEdit: false,
        general_info: {
          ...step1,
          adv_id: userInfo.org_id,
          org_id: step2[0]?.org_id,
          cost: Number.isFinite(payloadCost) ? Number(payloadCost.toFixed(2)) : totalCost,
          onBehalf: onBehalf,
        },
        zones: (zonesData || []).map((z) => ({
          ...z,
          isUsed: true,
        })),
        orgs: orgIds,
        origin: window.location.origin,
        comment: campComment,
        orgId: orgId,
        tempCampaignId: tempCampaignId || null,
        assignmentMappings: isNewFlow ? assignmentMappings : null,
      };

      const editZonesPayload = buildEditZonesPayload(zonesData, lockedDisplayIds, step2);

      const editCampaignPayload = {
        isEdit: true,
        campaign_id: campaignId,
        zones: editZonesPayload,
        tempCampaignId: tempCampaignId || null,
      };

      const toBeCreatedCampaign = isEdit ? editCampaignPayload : createCampaignPayload;

      if (isEdit && !campaignId) {
        throw new Error('campaign_id is required in edit mode');
      }
      if (isEdit) {
        const hasNewZones = editZonesPayload.some((z) => z?.isEdit === true);
        if (!hasNewZones) {
          throw new Error('No new zones provided for campaign edit');
        }
      }

      try {
        const response = await http.post(
          `savvy/campaigns/`,
          toBeCreatedCampaign
        );
        if (response?.data?.data) {
          dispatch.ProgressStepAdv.UpdateGetADVProgressStep(1);
        }
        this.clearStat();
      } catch (err) {
        this.clearStat();
        throw err;
      }
    },
    async previewCampaignExpansionHandler(data, state) {
      const { campaignId, lockedDisplayIds = [], costData } = data || {};
      if (!campaignId) {
        throw new Error('campaign_id is required in edit mode');
      }

      const { step2, listOfCampaignZones, assignmentMappings, mediaLibrary } = state.CampaignStepsMangment;
      const zonesData = buildZonesDataPayload({
        step2,
        listOfCampaignZones,
        assignmentMappings,
        mediaLibrary,
        costData,
      });
      const editZonesPayload = buildEditZonesPayload(zonesData, lockedDisplayIds, step2);

      const hasNewZones = editZonesPayload.some((z) => z?.isEdit === true);
      if (!hasNewZones) {
        throw new Error('No new zones provided for campaign edit');
      }

      const response = await http.post(
        `savvy/campaigns/${campaignId}/expansion/preview`,
        { zones: editZonesPayload }
      );

      return response?.data?.data || response?.data || {};
    },
    async confirmCampaignExpansionHandler(data, state) {
      const { campaignId, versionId, lockedDisplayIds = [], costData } = data || {};
      if (!campaignId) throw new Error('campaign_id is required for confirmation');
      if (!versionId) throw new Error('versionId is required for confirmation');

      const { step2, listOfCampaignZones, assignmentMappings, mediaLibrary } = state.CampaignStepsMangment;
      const zonesData = buildZonesDataPayload({
        step2,
        listOfCampaignZones,
        assignmentMappings,
        mediaLibrary,
        costData,
      });
      const editZonesPayload = buildEditZonesPayload(zonesData, lockedDisplayIds, step2);
      const newZonesOnly = editZonesPayload.filter((z) => z?.isEdit === true);

      if (newZonesOnly.length === 0) {
        throw new Error('No new zones provided for campaign confirmation');
      }

      const response = await http.post(
        `savvy/campaigns/${campaignId}/expansion/${versionId}/confirm`,
        { zones: newZonesOnly }
      );

      return response?.data?.data || response?.data || {};
    },
    async appendSelectedDisplayHandler(data, state) {
      const { selectedZones } =
        state.CampaignStepsMangment;

      let updatedZones;
      if (selectedZones.includes(data)) {
        updatedZones = selectedZones.filter((id) => id !== data);
      } else {
        updatedZones = [...selectedZones, data];
      }
      this.appendSelectedDisplay(updatedZones);

    },
    async removeSelectedDisplayHandler(state) {
      this.appendSelectedDisplay([]);
    },

    async clearAllData() {
      this.clearStat();
    },

    async clearStats() {
      this.clearScreenShot();
    },

    async fetchZoneGroups({ displayIds, tags }, state) {
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo'));
        const userId = userInfo?.id;

        if (!userId) {
          throw new Error('User information not found');
        }

        if (!displayIds || displayIds.length === 0) {
          throw new Error('Display IDs are required');
        }

        if (!tags || tags.length === 0) {
          throw new Error('Zone tags are required');
        }

        const url = 'savvy/campaign/groups/zones';
        const payload = {
          display_ids: displayIds,
          tags: tags,
          user_id: userId,
        };


        const response = await http.post(url, payload);


        if (!response.data?.success || response.data?.status === 404) {
          const errorMessage = response.data?.message || 'No zones found for the rate schedules assigned to these displays. Please create zones in the rate schedule.';
          throw new Error(errorMessage);
        }

        const zoneGroupsObject = response.data?.data?.zone_groups || {};
        const mediaLibrary = state.CampaignStepsMangment?.mediaLibrary || [];

        const transformedZoneGroups = Object.entries(zoneGroupsObject)
          .filter(([, displays]) => {
            return Array.isArray(displays) && displays.length > 0;
          })
          .map(([zoneType, displays]) => {
            const mediaForZone = mediaLibrary.filter(m => {
              const mediaZoneType = m.zoneType || m.fileUrl?.zoneType || null;
              return mediaZoneType && String(mediaZoneType).toLowerCase().trim() === String(zoneType).toLowerCase().trim();
            });

            let dimensions = { width: 0, height: 0 };
            let mediaType = 'video';
            let duration = null;

            if (mediaForZone.length > 0) {
              const firstMedia = mediaForZone[0];
              if (firstMedia.fileUrl?.width && firstMedia.fileUrl?.height) {
                dimensions = {
                  width: firstMedia.fileUrl.width,
                  height: firstMedia.fileUrl.height,
                };
              } else if (firstMedia.resolution) {
                const [width, height] = firstMedia.resolution.split('x').map(Number);
                dimensions = { width: width || 0, height: height || 0 };
              }

              mediaType = firstMedia.type?.startsWith('video/') ? 'video' : 'image';
              duration = firstMedia.duration || null;
            } else {
              const zoneTypeLower = String(zoneType).toLowerCase();
              if (zoneTypeLower.includes('horizontal') || zoneTypeLower.includes('primary-horizontal')) {
                dimensions = { width: 1920, height: 1080 };
              } else if (zoneTypeLower.includes('vertical') || zoneTypeLower.includes('primary-vertical')) {
                dimensions = { width: 1080, height: 1920 };
              } else if (zoneTypeLower.includes('square')) {
                dimensions = { width: 1080, height: 1080 };
              } else if (zoneTypeLower === 'ticker') {
                dimensions = { width: 1920, height: 80 };
              } else if (zoneTypeLower === 'fullscreen') {
                dimensions = { width: 1920, height: 1080 };
              }
            }

            let orientation = 'Horizontal';
            if (dimensions.width && dimensions.height) {
              if (dimensions.height > dimensions.width) {
                orientation = 'Vertical';
              } else if (Math.abs(dimensions.width - dimensions.height) < 50) {
                orientation = 'Square';
              } else if (dimensions.width / dimensions.height > 10) {
                orientation = 'Ticker';
              }
            }

            const hasAssignedMedia = mediaForZone.length > 0;

            const categoryDisplayName = getCategoryDisplayName(zoneType);
            const zoneDisplayName = categoryDisplayName || `Zone ${zoneType}`;

            return {
              zoneType: String(zoneType || '').toLowerCase().trim(),
              zoneDisplayName: zoneDisplayName,
              dimensions: dimensions,
              displayCount: displays.length,
              mediaType: mediaType,
              duration: duration,
              orientation: orientation,
              displays: displays.map(d => ({ id: d.id, name: d.name })),
              assignedMedia: hasAssignedMedia ? mediaForZone.map(m => ({
                mediaId: m.fileUrl?.mediaId || m.mediaId,
                fileName: m.name,
                type: m.type?.startsWith('video/') ? 'video' : 'image',
                duration: m.duration,
                dimensions: {
                  width: m.fileUrl?.width || (m.resolution ? Number(m.resolution.split('x')[0]) : 0),
                  height: m.fileUrl?.height || (m.resolution ? Number(m.resolution.split('x')[1]) : 0),
                },
              })) : [],
            };
          })
          .sort((a, b) => {
            if (b.displayCount !== a.displayCount) {
              return b.displayCount - a.displayCount;
            }
            return a.zoneType.localeCompare(b.zoneType);
          });

        if (Object.keys(zoneGroupsObject).length > 0 && transformedZoneGroups.length === 0) {
          throw new Error('No zones found that match selected display types and assigned rate layout types.');
        }

        return {
          success: true,
          zoneGroups: transformedZoneGroups,
        };
      } catch (err) {
        if (err.response?.status === 404 || err.response?.data?.status === 404) {
          const errorMessage = err.response?.data?.message || 'No zones found for the rate schedules assigned to these displays. Please create zones in the rate schedule.';
          throw new Error(errorMessage);
        }

        const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to fetch zone groups';
        throw new Error(errorMessage);
      }
    },

    async bulkAssignMediaToZoneGroup({ mediaId, zoneType, displayIds }, state, dispatch) {
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo'));
        const userId = userInfo?.id;
        const orgId = userInfo?.org_id;

        if (!userId) {
          throw new Error('User information not found');
        }

        if (!mediaId) {
          throw new Error('Media ID is required');
        }

        if (!zoneType) {
          throw new Error('Zone type is required');
        }

        if (!displayIds || displayIds.length === 0) {
          throw new Error('Display IDs are required');
        }

        const url = 'savvy/campaign/groups/assignments/bulk';
        const payloadFor = (zoneTypeValue) => ({
          mediaId: mediaId,
          zoneType: zoneTypeValue,
          displayIds: displayIds,
          user_id: userId,
          org_id: orgId,
        });

        let response;
        try {
          response = await http.post(url, payloadFor(zoneType));
        } catch (err) {
          const backendMsg = err?.response?.data?.error || err?.response?.data?.message || '';
          const isInvalidZoneType = String(backendMsg).toLowerCase().includes('invalid zone type');
          if (String(zoneType).toLowerCase().trim() === 'led' && isInvalidZoneType) {
            response = await http.post(url, payloadFor('custom'));
          } else {
            throw err;
          }
        }

        const responseData = response.data?.data || response.data || {};

        if (responseData.mediaId && responseData.zoneType) {
          const latestState = this.getState ? this.getState() : state;
          const currentMappings = latestState.CampaignStepsMangment?.assignmentMappings || {};
          const currentZoneMappings = currentMappings[responseData.zoneType] || {};
          const currentMappingsArray = currentZoneMappings.mappings || [];
          const newMappingsArray = responseData.mappings || [];

          const mappingsToUpdate = new Set(
            newMappingsArray.map(m => `${m.display_id}-${m.zone_id}`)
          );
          const filteredOldMappings = currentMappingsArray.filter(
            m => !mappingsToUpdate.has(`${m.display_id}-${m.zone_id}`)
          );
          const mergedMappings = [...filteredOldMappings, ...newMappingsArray];

          const uniqueDisplayIds = new Set(mergedMappings.map(m => m.display_id));
          const totalDisplayCount = uniqueDisplayIds.size;

          this.injectStepData({
            assignmentMappings: {
              ...currentMappings,
              [responseData.zoneType]: {
                mediaId: responseData.mediaId,
                zoneType: responseData.zoneType,
                mappings: mergedMappings,
                assignmentsCreated: mergedMappings.length,
                displayCount: totalDisplayCount,
              },
            },
          });
        }

        return {
          success: true,
          data: responseData,
        };
      } catch (err) {
        const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to assign media';
        throw new Error(errorMessage);
      }
    },

  }),
};

export default CampaignStepsMangment;